// D6/T2/T3: TerminalBlock 用 xterm.js 渲染 agent invocation 输出。
//
// T2 IME Bridge（docs/terminal-hardening-plan.html §2）：
//   xterm canvas 没有真实 caret，OS IME 候选窗会贴左上角 + 中文输入会逐字写 PTY。
//   方案：在 .xterm-frame 内放一个跟随光标的隐藏 <textarea class="ime-bridge">，
//   仅在 compositionend 一次性把完整中文写入 PTY。所有 ASCII / 控制键仍由 xterm
//   原生 textarea 处理。keydown 守卫 isComposing || keyCode===229 时吞掉，
//   但 Ctrl / Meta 组合永远绕过此判定（Ctrl+C 必须能中断）。
//
// T3 §3.3/3.5：
//   - cancelling 状态：点 ✕ 立即灰化、5 s 无 eof 切到"强制清理本地状态"
//   - resize debounce 250 ms trailing：拖窗口不再产生大量 resize 帧
//   - eof.reason 语义化文案：cancelled / timeout_kill / agent-disconnect / done
//   - stale 状态：重连后 backend 告知此 invocation 已不再 active，提示用户

import "@xterm/xterm/css/xterm.css";

import { useEffect, useRef, useState } from "react";

import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";

import { Avatar } from "./Avatar";

export interface TerminalBlockProps {
  adapter: string;
  invocationId: string;
  text: string;
  isComplete: boolean;
  ts: number;
  compact?: boolean;
  /** T3 §3.4 EOF reason：cancelled / timeout_kill / agent-disconnect / done */
  eofReason?: string;
  /** T3 §3.4 重连后 backend 告知此 invocation 已 dead，UI 提示"重新发起" */
  stale?: boolean;
  /** T3 §3.5 backend ack 的实际 cols/rows，用于不一致时兜底重发 */
  resizeAck?: { cols: number; rows: number };
  /** K9: 点 ✕ 触发取消 */
  onCancel?: (invocationId: string) => void;
  /** K10: 给正在运行的 PTY 追加 stdin */
  onInput?: (invocationId: string, data: string) => void;
  /** K10.1: 同步浏览器终端尺寸到本机 PTY */
  onResize?: (invocationId: string, cols: number, rows: number) => void;
}

// K11 mobile: 触屏 / 窄屏设备字号缩小, 让 cols 不至于太少而导致 TUI 崩
function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(hover: none) and (pointer: coarse)").matches ||
    window.innerWidth < 600
  );
}

// 主题: 跟随 chatbox 黑白专业风, 浅色背景 + 深色字; 暗色模式 CSS 切换
function makeTerminal(): Terminal {
  const mobile = isMobileViewport();
  return new Terminal({
    fontFamily:
      'Menlo, Monaco, "SF Mono", "JetBrains Mono", "PingFang SC", "Microsoft YaHei", Consolas, monospace',
    fontSize: mobile ? 10 : 13, // mobile: 10px -> ~40 cols on 360px viewport
    lineHeight: 1.0,
    convertEol: true,
    cursorBlink: true,
    cursorStyle: "bar",
    scrollback: 1000,
    allowProposedApi: true,
    theme: themeForRoot(),
  });
}

function themeForRoot() {
  const dark = isDark();
  return {
    background: dark ? "#0f1216" : "#fafafa",
    foreground: dark ? "#e8eaed" : "#111",
    cursor: dark ? "#e8eaed" : "#111",
    selectionBackground: dark ? "#475063" : "#c8ccd4",
    black:        "#000000",
    red:          dark ? "#f87171" : "#dc2626",
    green:        dark ? "#86efac" : "#16a34a",
    yellow:       dark ? "#fde68a" : "#ca8a04",
    blue:         dark ? "#93c5fd" : "#2563eb",
    magenta:      dark ? "#f0abfc" : "#c026d3",
    cyan:         dark ? "#67e8f9" : "#0891b2",
    white:        dark ? "#e2e8f0" : "#374151",
    brightBlack:  dark ? "#475569" : "#6b7280",
    brightRed:    "#fca5a5",
    brightGreen:  "#bbf7d0",
    brightYellow: "#fef3c7",
    brightBlue:   "#bfdbfe",
    brightMagenta:"#f5d0fe",
    brightCyan:   "#cffafe",
    brightWhite:  "#f8fafc",
  };
}

function isDark(): boolean {
  const attr = document.documentElement.getAttribute("data-theme");
  if (attr === "dark") return true;
  if (attr === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

// T3 §3.3: 取消请求最长等多久才提示"取消请求未响应"
const CANCEL_TIMEOUT_MS = 5000;
// T3 §3.5: resize 防抖（拖窗口 60 fps → 4 events/s）
// UI-bugfix: 250 ms 让用户拖窗口期间一直 pending，TUI 看不到 SIGWINCH → 视觉错位。
// 改 60 ms：足够避免每帧爆发（拖动 60fps = 16ms/帧，60ms 合并 ~4 帧），又足够实时让 TUI 跟随重绘。
const RESIZE_DEBOUNCE_MS = 60;

type CancelState = "idle" | "cancelling" | "unresponded";

export function TerminalBlock({
  adapter,
  invocationId,
  text,
  isComplete,
  ts,
  compact,
  eofReason,
  stale,
  resizeAck,
  onCancel,
  onInput,
  onResize,
}: TerminalBlockProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const bridgeRef = useRef<HTMLTextAreaElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const writtenRef = useRef(0);
  const onInputRef = useRef(onInput);
  const onResizeRef = useRef(onResize);
  const completeRef = useRef(isComplete);
  const [expanded, setExpanded] = useState(false);
  // C1-终端: 任务完成后用户点红钮可"折叠 body" (只剩 titlebar), 点 titlebar 再展开.
  // 非破坏性, 不杀进程不删消息, 跟 K9 取消 (杀进程) / ExpandableMedia (放大) 区分.
  const [collapsed, setCollapsed] = useState(false);
  const [cancelState, setCancelState] = useState<CancelState>("idle");
  const cancelTimerRef = useRef<number | null>(null);
  // T3 §3.5: 最后一次"已发送"的 cols/rows，用来 reconcile ack
  const lastSentSizeRef = useRef<{ cols: number; rows: number } | null>(null);
  // T2: 标记 compositionend 刚发生过，吞掉随后 Chromium "insertCompositionText" input
  const justEndedComposeRef = useRef(false);

  useEffect(() => {
    onInputRef.current = onInput;
    onResizeRef.current = onResize;
    completeRef.current = isComplete;
  }, [isComplete, onInput, onResize]);

  // ---- 初始化 terminal + IME bridge ----
  useEffect(() => {
    if (!hostRef.current) return;
    const term = makeTerminal();
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(hostRef.current);

    // 发送一次 resize；记录已发尺寸用于 ack reconcile
    const sendResize = (cols: number, rows: number) => {
      lastSentSizeRef.current = { cols, rows };
      onResizeRef.current?.(invocationId, cols, rows);
    };

    requestAnimationFrame(() => {
      try {
        fit.fit();
        sendResize(term.cols, term.rows);
      } catch {
        /* 容器还没量到尺寸 */
      }
    });

    const dataDisposable = term.onData((data) => {
      if (completeRef.current) return;
      onInputRef.current?.(invocationId, data);
      term.scrollToBottom();
    });

    // K10 mobile: 用 native addEventListener (capture) 抓 touchstart, 早于
    // xterm 内部 handler, 同步 focus bridge -> iOS Safari 弹软键盘。
    // React onTouchStart 是 synthetic 异步, iOS 软键盘不认。
    const onNativeTouch = () => {
      if (!isMobileViewport()) return;
      const bridge = bridgeRef.current;
      if (bridge && !completeRef.current) bridge.focus();
    };
    const host = hostRef.current;
    if (host) {
      host.addEventListener("touchstart", onNativeTouch, {
        passive: true,
        capture: true,
      });
    }
    const resizeDisposable = term.onResize(({ cols, rows }) => {
      sendResize(cols, rows);
    });
    termRef.current = term;
    fitRef.current = fit;

    // ---- UI-bugfix v4: 彻底切断 fit() 反馈循环 ----
    // 前三轮尝试都失败（监听 frame / 60ms debounce / 禁原生 scrollbar / 加 cols 兜底），
    // 用户依然报"打开终端就在不停 resize"。根因是 ResizeObserver 在 DOM 任何
    // sub-pixel 变化时都会 fire——React 初次 mount 时 layout 会反复抖几下，
    // 每次 fit 触发 onResize 又写回 PTY，TUI SIGWINCH 重绘又改 DOM → 死循环。
    //
    // 现在改成只听 window.resize（这是用户真正拖窗口时才 fire 的"硬"信号），
    // 不监听任何 DOM 元素。trade-off：sidebar 折叠之类不会触发 fit。
    // 但稳定 >> 即时。如果未来 sidebar 改宽需要重 fit，单独通过 props 触发。
    let resizeTimer: number | null = null;
    const flushFit = () => {
      resizeTimer = null;
      try {
        fit.fit();
        const term = termRef.current;
        // K11: 最小 cols 提到 40 —— 低于这值 claude/vim 等 TUI 在窄列下渲染崩
        // (ANSI escape 序列被截断成普通字符显示, box-drawing 也错位)
        // mobile 装得下 ~38 cols (fontSize=10 + viewport=360), 设 40 让它略大
        // 不够就由 .xterm-host overflow-x 横向滑
        const MIN_COLS = 40;
        const MIN_ROWS = 6;
        if (term && (term.cols < MIN_COLS || term.rows < MIN_ROWS)) {
          term.resize(Math.max(term.cols, MIN_COLS), Math.max(term.rows, MIN_ROWS));
        }
      } catch {
        /* ignore */
      }
    };
    const scheduleFit = () => {
      if (resizeTimer !== null) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(flushFit, RESIZE_DEBOUNCE_MS);
    };
    window.addEventListener("resize", scheduleFit);

    // 主题变化
    const mo = new MutationObserver(() => {
      term.options.theme = themeForRoot();
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    // ---- T2: cursor follow for IME bridge ----
    // 优先用 xterm 内部 renderService.dimensions.css.cell.{width,height}；
    // 不存在时降级用 measure span（永远有 fallback，对应 §2.4 风险）。
    function measureCellSize(): { w: number; h: number } | null {
      const core = (term as unknown as { _core?: any })._core;
      const dim = core?._renderService?.dimensions?.css?.cell;
      if (dim && typeof dim.width === "number" && typeof dim.height === "number" && dim.width > 0) {
        return { w: dim.width, h: dim.height };
      }
      // fallback: 找一个 xterm 渲染出来的 char 测尺寸
      const host = hostRef.current;
      if (!host) return null;
      const row = host.querySelector(".xterm-rows > div");
      if (row instanceof HTMLElement) {
        const rect = row.getBoundingClientRect();
        if (rect.height > 0 && term.cols > 0) {
          return { w: rect.width / term.cols, h: rect.height };
        }
      }
      return null;
    }

    let rafScheduled = false;
    const positionBridge = () => {
      rafScheduled = false;
      const bridge = bridgeRef.current;
      const host = hostRef.current;
      if (!bridge || !host) return;
      const size = measureCellSize();
      if (!size) return;
      // 用 buffer.active.cursorX/Y（viewport 内行号）
      const buf = term.buffer.active;
      const x = buf.cursorX * size.w;
      const y = buf.cursorY * size.h;
      bridge.style.transform = `translate(${x}px, ${y}px)`;
      bridge.style.width = `${Math.max(size.w, 1)}px`;
      bridge.style.height = `${Math.max(size.h, 1)}px`;
    };
    const scheduleBridge = () => {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(positionBridge);
    };
    const cursorDisposable = term.onCursorMove(scheduleBridge);
    const renderDisposable = term.onRender(scheduleBridge);
    // 初次定位
    scheduleBridge();

    return () => {
      if (resizeTimer !== null) window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", scheduleFit);
      if (host) {
        host.removeEventListener("touchstart", onNativeTouch, {
          capture: true,
        } as EventListenerOptions);
      }
      mo.disconnect();
      dataDisposable.dispose();
      resizeDisposable.dispose();
      cursorDisposable.dispose();
      renderDisposable.dispose();
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
      writtenRef.current = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // expanded 切换 → 重新 fit
  useEffect(() => {
    // collapsed 状态下 body 隐藏, 不做 fit (会算到 0 高度)
    if (collapsed) return;
    requestAnimationFrame(() => {
      try {
        fitRef.current?.fit();
      } catch {
        /* ignore */
      }
      // 修 bug: invocation 完成后切 expanded 不再 send resize 给 agent-host.
      // 之前会触发 PTY (claude) SIGWINCH -> TUI 重画 banner -> client term.write
      // 把新 banner 追加到原 stdout, 用户看到的就是 banner 重复 2/3 份.
      if (termRef.current && !completeRef.current) {
        onResizeRef.current?.(invocationId, termRef.current.cols, termRef.current.rows);
        lastSentSizeRef.current = {
          cols: termRef.current.cols,
          rows: termRef.current.rows,
        };
      }
      termRef.current?.focus();
    });
  }, [expanded, collapsed, invocationId]);

  // 流式追加 text 增量
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    if (text.length < writtenRef.current) {
      term.clear();
      writtenRef.current = 0;
    }
    if (text.length > writtenRef.current) {
      const delta = text.slice(writtenRef.current);
      term.write(delta, () => term.scrollToBottom());
      writtenRef.current = text.length;
    }
  }, [text]);

  // T3 §3.3: invocation 完成时清取消计时器；恢复 idle
  useEffect(() => {
    if (isComplete) {
      if (cancelTimerRef.current !== null) {
        window.clearTimeout(cancelTimerRef.current);
        cancelTimerRef.current = null;
      }
      setCancelState("idle");
    }
  }, [isComplete]);

  // T3 §3.5: 收到 resize ack 时若与最后一次发送不一致 → 兜底重发
  useEffect(() => {
    if (!resizeAck) return;
    const sent = lastSentSizeRef.current;
    if (!sent) return;
    if (sent.cols === resizeAck.cols && sent.rows === resizeAck.rows) return;
    // backend ack 的尺寸与本地最后发送不一致：可能有丢包/竞态。重发当前实际尺寸。
    const term = termRef.current;
    if (!term) return;
    onResizeRef.current?.(invocationId, term.cols, term.rows);
    lastSentSizeRef.current = { cols: term.cols, rows: term.rows };
  }, [resizeAck, invocationId]);

  // 卸载时清取消计时器
  useEffect(() => {
    return () => {
      if (cancelTimerRef.current !== null) {
        window.clearTimeout(cancelTimerRef.current);
        cancelTimerRef.current = null;
      }
    };
  }, []);

  function focusTerminal() {
    requestAnimationFrame(() => termRef.current?.focus());
  }

  function handleCancelClick() {
    if (!onCancel) return;
    if (cancelState !== "idle") return;
    setCancelState("cancelling");
    onCancel(invocationId);
    if (cancelTimerRef.current !== null) window.clearTimeout(cancelTimerRef.current);
    cancelTimerRef.current = window.setTimeout(() => {
      cancelTimerRef.current = null;
      // 若仍未收到 EOF，进入"未响应"态
      setCancelState((cur) => (cur === "cancelling" ? "unresponded" : cur));
    }, CANCEL_TIMEOUT_MS);
  }

  function handleForceLocalCleanup() {
    // 仅清前端状态：把按钮恢复 idle，不重发 cancel
    setCancelState("idle");
    if (cancelTimerRef.current !== null) {
      window.clearTimeout(cancelTimerRef.current);
      cancelTimerRef.current = null;
    }
  }

  // ---- T2: IME bridge 事件 ----
  // composing 中所有 key 都吞掉（含方向键/Enter/Backspace），但 Ctrl/Meta 绕过。
  function bridgeKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Ctrl / Meta 组合永远不算 composing 输入（Ctrl+C 中断必须能透传）
    if (e.ctrlKey || e.metaKey) {
      // 让 xterm 原生 textarea 处理：把焦点切回
      const native = e.nativeEvent as KeyboardEvent;
      if (native.isComposing || native.keyCode === 229) {
        // 极少数 IME 框架可能仍打 isComposing=true，这里强制结束 composing 然后转 xterm
      }
      // 把事件转发给 xterm 的 textarea
      const xtermTa = hostRef.current?.querySelector(
        ".xterm-helper-textarea",
      ) as HTMLTextAreaElement | null;
      if (xtermTa) {
        xtermTa.focus();
        // 不要 preventDefault；让事件冒泡到 xterm 处理
      }
      return;
    }
    const native = e.nativeEvent as KeyboardEvent;
    if (native.isComposing || native.keyCode === 229) {
      // composing 中 IME 自己处理候选窗翻页 / Enter 提交，不要透到 xterm
      e.stopPropagation();
      return;
    }
    // 非 composing 状态下的按键：把焦点交还给 xterm 让它原生处理
    const xtermTa = hostRef.current?.querySelector(
      ".xterm-helper-textarea",
    ) as HTMLTextAreaElement | null;
    if (xtermTa) {
      xtermTa.focus();
    }
  }

  function bridgeCompositionStart() {
    justEndedComposeRef.current = false;
    const bridge = bridgeRef.current;
    if (bridge) {
      // 让预览中间字符可见
      bridge.style.opacity = "0.9";
      bridge.style.color = "var(--fg, currentColor)";
    }
  }

  function bridgeCompositionEnd(e: React.CompositionEvent<HTMLTextAreaElement>) {
    const data = e.data ?? "";
    const bridge = bridgeRef.current;
    if (bridge) {
      bridge.value = "";
      bridge.style.opacity = "0.01";
      bridge.style.color = "transparent";
    }
    justEndedComposeRef.current = true;
    if (data && !completeRef.current) {
      onInputRef.current?.(invocationId, data);
      termRef.current?.scrollToBottom();
    }
    // 焦点交还 xterm（macOS：compositionend 同帧 Enter 不被吃掉的关键）
    const xtermTa = hostRef.current?.querySelector(
      ".xterm-helper-textarea",
    ) as HTMLTextAreaElement | null;
    setTimeout(() => {
      xtermTa?.focus();
      // 下一帧清空"刚结束 compose"标记
      justEndedComposeRef.current = false;
    }, 0);
  }

  // Chromium: compositionend 后会再触发 input event with inputType=insertCompositionText
  // 必须忽略避免双发
  //
  // K10: 桌面端走 xterm onData (keydown 已把焦点交还 xterm), 不会到这里。
  // 移动端没物理键盘 -> 软键盘输入触发 input event (不发 keydown), inputType:
  //   "insertText"            普通字符
  //   "insertLineBreak"       回车
  //   "deleteContentBackward" 退格
  //   "insertCompositionText" IME 预览 (composing) - 已被上面 isComposing 拦
  // 这条路径只在 mobile 真实生效, 桌面意外路径也安全 (input 已被 xterm 拦)
  function bridgeInput(e: React.FormEvent<HTMLTextAreaElement>) {
    const native = e.nativeEvent as InputEvent;
    if (native.isComposing) {
      // composing 中预览，不写 PTY
      return;
    }
    if (justEndedComposeRef.current) {
      // 吞掉 Chromium 的 trailing insertCompositionText
      const bridge = bridgeRef.current;
      if (bridge) bridge.value = "";
      return;
    }
    const bridge = bridgeRef.current;
    if (!bridge || completeRef.current) return;
    const value = bridge.value;
    const inputType = native.inputType;
    if (inputType === "insertText" && value) {
      onInputRef.current?.(invocationId, value);
      termRef.current?.scrollToBottom();
    } else if (inputType === "insertLineBreak" || inputType === "insertParagraph") {
      onInputRef.current?.(invocationId, "\r");
      termRef.current?.scrollToBottom();
    } else if (inputType === "deleteContentBackward") {
      onInputRef.current?.(invocationId, "\x7f");
    }
    bridge.value = "";
  }

  function bridgePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const text = e.clipboardData?.getData("text") ?? "";
    e.preventDefault();
    if (!text) return;
    if (completeRef.current) return;
    onInputRef.current?.(invocationId, text);
    termRef.current?.scrollToBottom();
  }

  // bridge 仅在用户开始 composing 时才需要承接输入；平时 pointer-events:none
  // 但需要键盘焦点可达。我们让它在 frame focus / mousedown 时 focus 自己，
  // 这样 IME 候选窗可以拿到位置；普通点击仍优先把焦点给 xterm。
  function frameMouseDown() {
    focusTerminal();
  }

  // K10: 触屏设备 (mobile) 点终端 -> focus xterm helper textarea 弹出软键盘
  // 必须在 touchstart 的 user gesture 内同步 focus, 否则 iOS Safari 不认.
  // focus 后下一帧 scrollIntoView, 让 frame 顶到 visualViewport 内, 防键盘遮挡.
  function frameTouchStart() {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) return;
    if (completeRef.current) return;
    const xtermTa = hostRef.current?.querySelector(
      ".xterm-helper-textarea",
    ) as HTMLTextAreaElement | null;
    // 优先 xterm helper (raw mode 直送 PTY), 否则降级到 bridge (IME 路径)
    (xtermTa ?? bridgeRef.current)?.focus();
    // iOS Safari 在 keyboard 弹起后 ~300ms 才稳定. 等一会再 scroll
    setTimeout(() => {
      frameRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 350);
  }

  // ---- 状态文案 ----
  const statusLine = computeStatusLine({
    isComplete,
    eofReason,
    cancelState,
    stale,
  });

  const showCancelButton = !isComplete && onCancel;

  return (
    <li
      className={`bubble bubble--peer bubble--term${compact ? " bubble--compact" : ""}${
        expanded ? " bubble--term-expanded" : ""
      }${collapsed ? " bubble--term-collapsed" : ""}`}
    >
      {!compact && <Avatar seed={adapter} label={adapter} size={32} />}
      {compact && <span className="bubble__avatar-spacer" />}
      <div className="bubble__col bubble__col--wide">
        {!compact && (
          <header className="bubble__meta">
            <span className="bubble__sender bubble__sender--agent">{adapter}</span>
            <span
              className={
                isComplete
                  ? "term-state term-state--done"
                  : stale
                  ? "term-state term-state--stale"
                  : "term-state term-state--running"
              }
            >
              {isComplete ? "✓ 完成" : stale ? "已断开" : "运行中…"}
            </span>
            <time className="bubble__ts">{formatTime(ts)}</time>
            <code className="bubble__inv-id">{invocationId.slice(0, 10)}</code>
            {showCancelButton && cancelState === "unresponded" && (
              <button
                type="button"
                className="term-cancel term-cancel--force"
                onClick={handleForceLocalCleanup}
                title="取消请求未响应 —— 仅清理本地状态"
                aria-label="强制清理本地状态"
              >
                强制清理
              </button>
            )}
          </header>
        )}
        <div className="bubble__body bubble__body--term">
          <div
            ref={frameRef}
            className="xterm-frame"
            role="textbox"
            tabIndex={0}
            aria-label={`${adapter} 交互终端`}
            onMouseDown={frameMouseDown}
            onTouchStart={frameTouchStart}
            onFocus={focusTerminal}
          >
            {/* macOS 风格 titlebar:
                红=进行中→取消 invocation; 完成后→折叠 body (B 方案)
                绿=放大/收起 (改变 expanded). collapsed 时点 titlebar 整条展开 */}
            <div
              className="xterm-titlebar"
              aria-hidden={false}
              onClick={collapsed ? () => setCollapsed(false) : undefined}
              role={collapsed ? "button" : undefined}
              title={collapsed ? "点击展开终端" : undefined}
              style={collapsed ? { cursor: "pointer" } : undefined}
            >
              {showCancelButton && cancelState === "idle" && (
                <button
                  type="button"
                  className="xterm-dot xterm-dot--close"
                  onClick={(e) => { e.stopPropagation(); handleCancelClick(); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="取消此次调用"
                  aria-label="取消"
                />
              )}
              {showCancelButton && cancelState === "cancelling" && (
                <button
                  type="button"
                  className="xterm-dot xterm-dot--close xterm-dot--pending"
                  disabled
                  title="取消请求已发出，等待响应"
                  aria-label="正在取消"
                />
              )}
              {!showCancelButton && !collapsed && (
                // 完成后红钮 = 折叠 body (B 方案)
                <button
                  type="button"
                  className="xterm-dot xterm-dot--close"
                  onClick={(e) => { e.stopPropagation(); setCollapsed(true); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="折叠终端 (历史保留, 点 titlebar 展开)"
                  aria-label="折叠终端"
                />
              )}
              {collapsed && (
                <span className="xterm-dot xterm-dot--placeholder" aria-hidden="true" />
              )}
              {!collapsed && (
                <button
                  type="button"
                  className="xterm-dot xterm-dot--zoom"
                  onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  title={expanded ? "收起终端" : "放大终端"}
                  aria-label={expanded ? "收起终端" : "放大终端"}
                />
              )}
              {collapsed && (
                <span className="xterm-collapsed-hint muted small">
                  {adapter} · 已折叠 (点击展开)
                </span>
              )}
            </div>
            <div className="xterm-host" ref={hostRef} />
            {/* T2: IME bridge —— 跟随光标的隐藏 textarea */}
            <textarea
              ref={bridgeRef}
              className="ime-bridge"
              aria-hidden="true"
              tabIndex={-1}
              autoCapitalize="off"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              inputMode="text"
              onKeyDown={bridgeKeyDown}
              onCompositionStart={bridgeCompositionStart}
              onCompositionUpdate={() => {
                /* 仅本地预览，PTY 不写。compositionend 一次性写入 */
              }}
              onCompositionEnd={bridgeCompositionEnd}
              onInput={bridgeInput}
              onPaste={bridgePaste}
            />
          </div>
          {statusLine && (
            <div className={`term-status-line term-status-line--${statusLine.tone}`}>
              {statusLine.text}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function computeStatusLine(args: {
  isComplete: boolean;
  eofReason?: string;
  cancelState: CancelState;
  stale?: boolean;
}): { text: string; tone: "info" | "warn" | "bad" } | null {
  const { isComplete, eofReason, cancelState, stale } = args;
  if (isComplete) {
    switch (eofReason) {
      case "cancelled":
        return { text: "已取消", tone: "warn" };
      case "timeout_kill":
        return { text: "已取消（强制结束）", tone: "warn" };
      case "agent-disconnect":
        return { text: "Agent 已断开", tone: "bad" };
      case "done":
      case undefined:
      case "":
        return { text: "已完成", tone: "info" };
      default:
        return { text: `已结束（${eofReason}）`, tone: "info" };
    }
  }
  if (stale) {
    return { text: "Agent 已断开 —— 可重新发起", tone: "bad" };
  }
  if (cancelState === "cancelling") {
    return { text: "正在取消…", tone: "warn" };
  }
  if (cancelState === "unresponded") {
    return { text: "取消请求未响应，可点上方按钮强制清理本地状态", tone: "bad" };
  }
  return null;
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
